import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  CustomExtendedDataField,
  AspectRatioWrapper,
  H2,
  NamedLink,
  FieldRadioButton,
} from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';
import img1 from './images/sleek-doodle.svg';
import img2 from './images/strolling-doodle.svg';
import studentImg1 from './images/dancing-doodle.svg';
import studentImg2 from './images/sitting-doodle.svg';

import css from './SignupForm.module.css';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        preselectedUserType,
        userTypes,
        userFields,
        values,
        pathParams,
        loginOrSignupError,
      } = formRenderProps;

      const { userType } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;
      const isTutor = pathParams.userType === 'tutor';

      const linksLabel = <FormattedMessage id="SignupForm.linksLabel" />;
      const or = <FormattedMessage id="SignupForm.or" />;

      const loginLink = (
        <NamedLink name="LoginPage" className={css.link}>
          <FormattedMessage id="PasswordRecoveryForm.loginLinkText" />
        </NamedLink>
      );

      const signupLink = isTutor ? (
        <>
          {or}
          <NamedLink name="SignupPage" className={css.link}>
            <FormattedMessage id="LoginForm.SignupLinkText" />
          </NamedLink>
        </>
      ) : null;

      const nameRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.fullnameRequired',
        })
      );

      const nameValid = validators.nameValid(
        intl.formatMessage({
          id: 'SignupForm.fullnameInvalid',
        })
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.container}>
            <div className={css.left}>
              <AspectRatioWrapper className={css.aspectRatioWrapper}>
                <img className={css.rootForImage} alt="doodle" src={isTutor ? img1 : studentImg1} />
              </AspectRatioWrapper>
              <AspectRatioWrapper className={css.aspectRatioWrapper}>
                <img className={css.rootForImage} alt="doodle" src={isTutor ? img2 : studentImg2} />
              </AspectRatioWrapper>
            </div>
            <div className={css.right}>
              <H2 className={css.title}>
                <FormattedMessage id="SignupForm.createAccount" />
              </H2>
              {/* <FieldSelectUserType
                name="userType"
                userTypes={userTypes}
                hasExistingUserType={!!preselectedUserType}
                intl={intl}
              /> */}

              {showDefaultUserFields ? (
                <div className={css.defaultUserFields}>
                  <div className={css.name}>
                    <FieldTextInput
                      className={css.firstNameRoot}
                      type="text"
                      id={formId ? `${formId}.fname` : 'fname'}
                      name="fname"
                      autoComplete="given-name"
                      label={intl.formatMessage({
                        id: 'SignupForm.firstNameLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.firstNamePlaceholder',
                      })}
                      validate={validators.required(
                        intl.formatMessage({
                          id: 'SignupForm.firstNameRequired',
                        })
                      )}
                    />
                    <FieldTextInput
                      className={css.lastNameRoot}
                      type="text"
                      id={formId ? `${formId}.lname` : 'lname'}
                      name="lname"
                      autoComplete="family-name"
                      label={intl.formatMessage({
                        id: 'SignupForm.lastNameLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.lastNamePlaceholder',
                      })}
                      validate={validators.required(
                        intl.formatMessage({
                          id: 'SignupForm.lastNameRequired',
                        })
                      )}
                    />
                  </div>

                  <FieldTextInput
                    className={css.email}
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    autoComplete="email"
                    label={intl.formatMessage({
                      id: 'SignupForm.emailLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.emailPlaceholder',
                    })}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />

                  <UserFieldDisplayName
                    formName="SignupForm"
                    className={css.row}
                    userTypeConfig={userTypeConfig}
                    intl={intl}
                  />

                  <FieldTextInput
                    className={css.password}
                    type="password"
                    id={formId ? `${formId}.password` : 'password'}
                    name="password"
                    autoComplete="new-password"
                    label={intl.formatMessage({
                      id: 'SignupForm.passwordLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.passwordPlaceholder',
                    })}
                    validate={passwordValidators}
                  />
                  {!isTutor ? (
                    <div className={css.registerAsWrapper}>
                      <label className={css.registerAsLabel}>
                        <FormattedMessage id="SignupForm.iam" />
                      </label>
                      <div className={css.registerAs}>
                        <FieldRadioButton
                          id={`${formId}-parent`}
                          name="userType"
                          label={intl.formatMessage({
                            id: 'SignupForm.parent',
                          })}
                          labelClassName={css.registerAsOption}
                          checkedClassName={css.registerAsOptionSelected}
                          className={css.parent}
                          value="parent"
                          showAsRequired={true}
                        />
                        <FieldRadioButton
                          id={`${formId}-student`}
                          name="userType"
                          label={intl.formatMessage({
                            id: 'SignupForm.student',
                          })}
                          labelClassName={css.registerAsOption}
                          checkedClassName={css.registerAsOptionSelected}
                          className={css.student}
                          value="student"
                          showAsRequired={true}
                        />
                      </div>
                    </div>
                  ) : null}

                  <UserFieldPhoneNumber
                    formName="SignupForm"
                    className={css.row}
                    userTypeConfig={userTypeConfig}
                    intl={intl}
                  />
                </div>
              ) : null}

              {showCustomUserFields ? (
                <div className={css.customFields}>
                  {userFieldProps.map(({ key, ...fieldProps }) => (
                    <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                  ))}
                </div>
              ) : null}

              <div className={css.bottomWrapper}>
                {termsAndConditions}
                {loginOrSignupError}
                <PrimaryButton
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="SignupForm.signUp" />
                </PrimaryButton>
              </div>
              <p className={css.links}>
                <FormattedMessage
                  id="SignupForm.links"
                  values={{ linksLabel: linksLabel, loginLink: loginLink, signupLink, or: or }}
                />
              </p>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
